/**
 * Pure notification-targeting predicates.
 *
 * Intentionally Firebase-free and side-effect-free so the targeting rules are
 * single-source, trivially reviewable, and safe to import from any service.
 */

// Club membership roles that count as leadership / E-Board for the purpose of
// role-targeted notifications (e.g. the E-Board chat). Admins are leadership too.
const EBOARD_ROLES = ["e-board", "admin"];

/**
 * club-eboard-permission-parity: is this club_members.role an "admin or
 * e-board" leader — i.e. does it get operational parity with admin for
 * every action EXCEPT role changes, admin transfer, removing a fellow
 * leader, and deleting the club (those stay admin-only, checked directly
 * against "admin" wherever they're gated). Reuses EBOARD_ROLES rather than
 * a second constant, since the two lists are identical by construction.
 *
 * @param {string|null|undefined} role  club_members.role for the member
 * @returns {boolean}
 */
export function isClubLeaderRole(role) {
  return EBOARD_ROLES.includes(role);
}

/**
 * Should a member with `role` receive a notification restricted to `allowedRoles`?
 *
 * - If `allowedRoles` is missing/empty, there is no restriction → always true
 *   (preserves existing all-members behaviour for non-targeted notifications).
 * - A missing/unknown role is treated as the baseline "member".
 *
 * @param {string|null|undefined} role  club_members.role for the member
 * @param {string[]|null|undefined} allowedRoles  roles permitted to receive
 * @returns {boolean}
 */
export function shouldNotifyMemberRole(role, allowedRoles) {
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
  const normalized = role || "member";
  return allowedRoles.includes(normalized);
}

/**
 * Is this event_attendees record a CONFIRMED RSVP?
 *
 * The event_attendees collection holds both RSVPs and check-ins on the same
 * document. A confirmed RSVP is exactly status === "attending" (check-in
 * records also carry status "attending", so they remain included — desired).
 *
 * @param {{status?: string}|null|undefined} attendee
 * @returns {boolean}
 */
export function isConfirmedRsvp(attendee) {
  return !!attendee && attendee.status === "attending";
}
